import { useCallback, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface ConfirmState {
  title: string;
  message: string;
  confirmLabel: string;
}

/**
 * Promise-based styled confirm + text prompt, replacing map.html's hand-rolled
 * backdrop modals. Render the returned elements once; call confirm()/prompt()
 * from any handler.
 */
export const useDealDialogs = () => {
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [promptState, setPromptState] = useState<{ title: string } | null>(
    null,
  );
  const [promptValue, setPromptValue] = useState("");
  const confirmResolve = useRef<(ok: boolean) => void>(() => {});
  const promptResolve = useRef<(value: string | null) => void>(() => {});

  const confirm = useCallback(
    (title: string, message: string, confirmLabel = "Confirm") =>
      new Promise<boolean>((resolve) => {
        confirmResolve.current = resolve;
        setConfirmState({ title, message, confirmLabel });
      }),
    [],
  );

  const prompt = useCallback(
    (title: string) =>
      new Promise<string | null>((resolve) => {
        promptResolve.current = resolve;
        setPromptValue("");
        setPromptState({ title });
      }),
    [],
  );

  const closeConfirm = (ok: boolean) => {
    setConfirmState(null);
    confirmResolve.current(ok);
  };
  const closePrompt = (value: string | null) => {
    setPromptState(null);
    promptResolve.current(value);
  };

  const dialogs = (
    <>
      <Dialog
        open={!!confirmState}
        onOpenChange={(open) => !open && closeConfirm(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmState?.title}</DialogTitle>
            <DialogDescription>{confirmState?.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => closeConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={() => closeConfirm(true)}>
              {confirmState?.confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!promptState}
        onOpenChange={(open) => !open && closePrompt(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{promptState?.title}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="List name"
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") closePrompt(promptValue.trim() || null);
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => closePrompt(null)}>
              Cancel
            </Button>
            <Button onClick={() => closePrompt(promptValue.trim() || null)}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return { confirm, prompt, dialogs };
};
